import { useState, useEffect } from 'react';
import { ContractTransaction } from 'ethers';
import { useWallet } from 'components/WalletContext';
import { getEtherscanLink } from 'utils/helpers';
import { ERC1155PairsRewardStaking, ERC1155PairsRewardStakingFactory } from '../contracts/staking';
import { checkDeployment } from 'utils/contractHelpers';
import { STAKE_CONFIG } from 'components/Staking/stakeConfig';
import { PairInfo } from 'components/common';
import useLegacyIsRightNetwork from './useLegacyIsRightNetwork';

const useStakingContracts = () => {
  const [loading, setLoading] = useState(false);
  const { web3Provider, notify, address } = useWallet();
  const isRightNetwork = useLegacyIsRightNetwork();

  const [contract, setContract] = useState<ERC1155PairsRewardStaking>();
  const [contractsReady, setContractsReady] = useState<boolean>(false);

  const contractAddress = STAKE_CONFIG.contractAddress;

  // SETUP CONTRACTS
  useEffect(() => {
    async function loadContracts() {
      setLoading(true);

      try {
        if (web3Provider && contractAddress) {
          console.log('Loading staking contracts');
          const isContract = await checkDeployment(web3Provider, contractAddress);
          if (!isContract) throw Error('The provided address is not a contract');

          const stakingContract = ERC1155PairsRewardStakingFactory.connect(
            contractAddress,
            web3Provider.getSigner()
          );
          setContract(stakingContract);
          setContractsReady(true);
        }
      } catch (e) {
        console.log('ERROR LOADING STAKING CONTRACTS:', e);
      } finally {
        setLoading(false);
      }
    }

    loadContracts();
  }, [web3Provider, contractAddress]);

  // ===============================================================================================
  // HELPERS
  // ===============================================================================================
  function checkContractAndNetwork() {
    if (!isRightNetwork) throw new Error('Wrong network');
    if (!contract) throw new Error(`Contract Staking not setup yet`);
    console.log('contract address', contract.address);
  }

  // ===============================================================================================
  // GETTERS
  // ===============================================================================================

  async function getAllPairs(): Promise<PairInfo[]> {
    checkContractAndNetwork();
    const pairs = await contract!.getAllPairs();
    return pairs.map(([tokenIdA, tokenIdB, enabled], index) => ({
      genesisId: tokenIdA.toString(),
      enigmaId: tokenIdB.toString(),
      enabled,
      pairId: index,
    }));
  }

  async function getStakedBalanceOfBatch(account: string, contracts: string[], tokenIds: string[]) {
    checkContractAndNetwork();
    console.log('Getting staked balances for address', account);
    return await contract!.balanceOfBatch(account, contracts, tokenIds);
  }

  async function getTotalStaked() {
    checkContractAndNetwork();
    console.log('Getting total staked');
    return await contract!.numStakedPairs();
  }

  async function getPendingRewards(account: string) {
    checkContractAndNetwork();
    console.log('Getting pending rewards');
    return await contract!.accountPendingReward(account);
  }

  async function getMaxPairs() {
    checkContractAndNetwork();
    console.log('Getting max pairs');
    return await contract!.maxPairs();
  }

  // ===============================================================================================
  // TRANSACTIONS
  // ===============================================================================================
  async function stake(pairId: number, amount: number, cb: any = () => {}) {
    checkContractAndNetwork();
    if (pairId !== 0 && !pairId) throw new Error('Missing pair id');
    if (!amount) throw new Error('Amount must be greater than 0');

    // TODO: check if pair is enabled???
    // const isEnabled = await isBurningEnabled(contract!);
    // if (!isEnabled) throw new Error('burning is disabled');

    const res = await contract!.stake(pairId, amount);
    handleEmitter(res, cb);
  }

  async function unstake(pairId: number, amount: number, cb: any = () => {}) {
    checkContractAndNetwork();
    if (pairId !== 0 && !pairId) throw new Error('Missing pair id');
    if (!amount) throw new Error('Amount must be greater than 0');

    const res = await contract!.unstake(pairId, amount);
    handleEmitter(res, cb);
  }

  async function claimReward(cb: any = () => {}) {
    checkContractAndNetwork();

    const pendingRewards = await getPendingRewards(address!);
    if (!pendingRewards.gt(0)) throw new Error('No rewards to claim');

    const res = await contract!.claimReward();
    handleEmitter(res, cb);
  }

  // ===============================================================================================
  // HELPERS
  // ===============================================================================================
  function handleEmitter(res: ContractTransaction, cb: any = () => {}) {
    const { emitter } = notify.hash(res.hash);

    emitter.on('txConfirmed', (tx: any) => {
      console.log('tx:', tx);
      if (tx.status === 'confirmed') {
        const link = getEtherscanLink(tx.hash);
        // Triggers success modal
        cb({ link, ...tx });
      }
      return {
        type: 'success',
        message:
          'Your transaction has succeeded. The UI may take a few moments to reflect these changes.',
        autoDismiss: 10000,
      };
    });
  }

  return {
    contractsReady,
    getAllPairs,
    loading,
    getStakedBalanceOfBatch,
    stake,
    unstake,
    getTotalStaked,
    getPendingRewards,
    claimReward,
    getMaxPairs,
  };
};

export default useStakingContracts;
